<template>
  <div>
    <template v-if="hasValidAccount">
      <div
        v-if="loaded"
        class="top-content-bar">
        <div class="wrapper pt-3 pb-1">
          <div class="text-wrapper">
            {{ $t('promotions.promotionTooltipInfo') }}
            <VDropdown class="inline-block">
              <span class="text-light-blue" style="cursor: pointer">{{ $t('promotions.seeWhereYourProfileWillAppear') }}</span>
              <template #popper>
                <img
                  v-if="langId === 2"
                  src="@/assets/images/promotion-slot-img-EN.png"
                  :alt="$t('promotions.profilePlacement')">
                <img
                  v-else
                  src="@/assets/images/promotion-slot-img.png"
                  :alt="$t('promotions.profilePlacement')">
              </template>
            </VDropdown>
          </div>
        </div>
      </div>

      <div :class="{'wrapper mt-3': true, 'is-loading': !loaded}">
        <f-list
          ref="slots-list"
          class="slots-list"
          :loaded="loaded"
          :listSettings="slotsListSettings"
          :listItems="slotsFormatted"
          :data-mobile-actions-count="1" />
      </div>

      <pagination
        :hasWrapper="true"
        :meta-data="paginationMetaData"
        :elementsCounterLabel="'elements'" />
    </template>
    <div
      v-if="!loaded && !loadError"
      class="loading-spinner-wrapper">
      <div class="loading-spinner pb-3 pt-3">
        <span class="loading-spinner-content">
          {{ $t('ui.loadingDataInProgress') }}&hellip;
        </span>
      </div>
    </div>
    <div class="wrapper mt-3">
      <f-alert
        v-if="!hasValidAccount"
        visible
        class="my-5"
        theme="inactive">
        <span>
        {{ $t('settlements.sectionAvailableForActiveUsers') }}
        </span>
      </f-alert>
      <f-alert
        v-else-if="loadError"
        visible
        theme="warning">
        <span>
          {{ $t('ui.dataLoadError.dataErrorMess') }} <a href="javascript:window.location.reload();" class="lnk lnk-alert">{{ $t('ui.dataLoadError.resetPageButton') }}</a> {{ $t('ui.dataLoadError.forTryAgain') }}
        </span>
      </f-alert>
    </div>
    <promotion-bid-popup />
  </div>
</template>

<script>
import Vue from 'vue';
import { mapGetters } from 'vuex';
import ListUtils from '@/utils/ListUtils.js';
import CalendarUtils from '@/utils/CalendarUtils.js';
import Pagination from '@/components/core/Pagination.vue';
import HasPagination from '@/mixins/HasPagination.vue';
import SlotsStatuses from '@/data/slots-statuses.js';
import PromotionBidPopup from '@/components/popups/PromotionBidPopup.vue';

export default {
  name: 'promotion-slots-list',
  mixins: [
    HasPagination
  ],
  components: {
    'pagination': Pagination,
    'promotion-bid-popup': PromotionBidPopup,
  },
  computed: {
    ...mapGetters({
      langId: 'getUserLanguageId'
    }),
    slotsStatuses () {
      return SlotsStatuses;
    },
    slotsListSettings () {
      let settingList = [
        {
          title: 'ID',
          variableName: ['id'],
          type: 'String',
          sortable: false
        },
        {
          title: this.$t('user.status'),
          variableName: ['statusFormatted'],
          type: 'f-badge',
          hasTooltip: true,
          tooltipImage: require('@/assets/images/promotion-slot-img.png'),
          tooltipImageAlt: this.$t('promotions.profilePlacement')
        },
        {
          title: this.$t('promotions.name'),
          variableName: ['name'],
          type: 'String'
        },
        {
          title: this.$t('promotions.highestBid'),
          variableName: ['maximalPrice'],
          type: 'String'
        },
        {
          title: this.$t('promotions.amIWinning'),
          variableName: ['isCurrentUserWinning'],
          type: 'String'
        },
        {
          title: this.$t('promotions.endsAt'),
          variableName: ['presentationTo'],
          emptyValue: '-',
          type: 'String'
        },
        {
          title: '',
          variableName: ['actionsList'],
          type: 'actions',
          items: [
            {
              type: 'button',
              disabled: false,
              event: 'slot-auction-open',
              label: this.$t('promotions.bidPromotion'),
              name: 'start'
            },
            {
              type: 'button',
              disabled: false,
              event: 'slot-auction-open',
              label: this.$t('promotions.outbidPromotion'),
              name: 'bid'
            },
            {
              type: 'button',
              disabled: false,
              event: 'slot-auction-open',
              label: this.$t('promotions.pay'),
              name: 'pay'
            }
          ]
        }
      ];

      return settingList;
    },
    slotsFormatted () {
      let slotsList = this.slots;

      if (!slotsList.length) {
        return [];
      }

      return slotsList.map(item => {
        item.isCurrentUserWinning = item.current_user_winning ? this.$t('ui.yes') : this.$t('ui.no');
        item.presentationTo = null;
        item.maximalPrice = null;

        item.actionsList = [];
        if (item.status === 'FREE') {
          item.actionsList.push('start');
        } else if (item.status === 'AUCTION') {
          item.actionsList.push('bid');
          item.maximalPrice = item.maximal_price + ' PLN';
        } else {
          item.isCurrentUserWinning = item.current_user_presentation ? this.$t('ui.yes') : this.$t('ui.no');
          if (item.waiting_payment) {
            item.isCurrentUserWinning = this.$t('ui.yes');
            item.actionsList.push('pay');
          }
          if (item.presentation_to) {
            item.presentationTo = CalendarUtils.prepareDateString(item.presentation_to);
          }
        }

        let itemStatus = this.slotsStatuses.find(element => element.value === item.status);
        item.statusFormatted = {
          status: itemStatus.badgeTheme,
          text: itemStatus.text
        }

        return item;
      });
    },
  },
  data () {
    return {
      hasValidAccount: null,
      loaded: false,
      loadError: false,
      removeInProgress: false,
      slots: []
    }
  },
  mounted() {
    this.$bus.$on('slot-auction-open', this.openBidPopup);
    this.$bus.$on('promotion-slots-list-reload', this.loadData);
    Vue.set(this, 'hasValidAccount', this.$store.getters['getUserHasValidAccount']);
    if (this.hasValidAccount) {
      this.loadData();
      return;
    }

    Vue.set(this, 'loaded', true);
  },
  methods: {
    loadData () {
      this.loadError = false;
      this.loaded = false;
      ListUtils.loadItemsData(this, {
        method: 'get',
        endpoint: '/api/adviser/slots/items?page=' + this.currentPage,
        listField: 'slots',
        pagination: true
      });
    },
    openBidPopup (slotItem) {
      this.$bus.$emit('promotion-bid-popup-show', slotItem.id);
    }
  },
  beforeDestroy () {
    this.$bus.$off('slot-auction-open', this.openBidPopup);
    this.$bus.$off('promotion-slots-list-reload', this.loadData);
  }
}
</script>
